import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, Eye, Mail, Send, Users, Calendar, Upload, Trash2,
  Edit2, Check, X, Image as ImageIcon, Link as LinkIcon, Code, FileText
} from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axiosInstance from '../config/axios';
import { API_BASE_URL } from '../types/review';

interface EmailAsset {
  id: number;
  asset_type: string;
  file_name: string;
  file_size?: number;
  mime_type?: string;
  content?: string;
}

export default function EmailEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [content, setContent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Email fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [subjectLine, setSubjectLine] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailType, setEmailType] = useState('newsletter');
  const [recipientsCount, setRecipientsCount] = useState<number>(0);
  const [scheduledFor, setScheduledFor] = useState('');

  // Assets
  const [assets, setAssets] = useState<EmailAsset[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);

  useEffect(() => {
    fetchContent();
  }, [id]);

  const fetchContent = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await axiosInstance.get(`/content/${id}`);
      const data = response.data;

      setContent(data);
      setTitle(data.title || '');
      setDescription(data.description || '');
      setScheduledFor(data.scheduled_for ? data.scheduled_for.split('T')[0] : '');

      // Parse metadata
      let metadata: any = {};
      try {
        metadata = data.metadata ? JSON.parse(data.metadata) : {};
      } catch (e) {
        console.warn('Failed to parse metadata');
      }

      setEmailType(metadata.email_type || 'newsletter');
      setSubjectLine(metadata.subject_line || data.title || '');
      setPreviewText(metadata.preview_text || data.description || '');
      setRecipientsCount(metadata.recipients_count || 0);

      // Get email body from assets or metadata
      const bodyAsset = data.assets?.find((a: any) => a.asset_type === 'email_html' || a.asset_type === 'email_body');
      if (bodyAsset?.content) {
        setEmailBody(bodyAsset.content);
      } else if (metadata.email_body) {
        setEmailBody(metadata.email_body);
      }

      setAssets(data.assets || []);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load email');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!content) return;

    setSaving(true);
    try {
      const metadata = {
        email_type: emailType,
        subject_line: subjectLine,
        preview_text: previewText,
        recipients_count: recipientsCount,
        email_body: emailBody
      };

      await axiosInstance.put(`/content/${content.id}`, {
        title,
        description,
        scheduled_for: scheduledFor || null,
        metadata: JSON.stringify(metadata)
      });

      // Update email body asset if it exists, otherwise create one
      const bodyAsset = assets.find(a => a.asset_type === 'email_html' || a.asset_type === 'email_body');
      if (bodyAsset) {
        await axiosInstance.put(`/assets/${bodyAsset.id}`, {
          content: emailBody
        });
      } else {
        // Create new email body asset
        await axiosInstance.post('/assets', {
          content_id: content.id,
          asset_type: 'email_html',
          file_name: 'email_body.html',
          content: emailBody,
          mime_type: 'text/html'
        });
      }

      alert('Email saved successfully!');
      await fetchContent();
    } catch (err: any) {
      alert('Save failed: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setUploadingFiles(files);
    }
  };

  const handleUpload = async () => {
    if (uploadingFiles.length === 0 || !content) return;

    setSaving(true);
    try {
      for (const file of uploadingFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('content_id', content.id);
        formData.append('asset_type', 'email_attachment');

        await axiosInstance.post('/assets/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      setUploadingFiles([]);
      await fetchContent();
    } catch (err: any) {
      alert('Upload failed: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAsset = async (assetId: number) => {
    if (!confirm('Delete this attachment?')) return;

    setSaving(true);
    try {
      await axiosInstance.delete(`/assets/${assetId}`);
      setAssets(assets.filter(a => a.id !== assetId));
    } catch (err: any) {
      alert('Delete failed: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      ['link', 'image'],
      ['clean']
    ]
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Email not found'}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const attachments = assets.filter(a => a.asset_type === 'email_attachment');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-20 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">Edit Email</h1>
                  <p className="text-sm text-gray-500">{emailType || 'Newsletter'}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/dashboard/content/${id}/preview`)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                Preview
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Email'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Email Settings */}
          <div className="lg:col-span-1 space-y-6">
            {/* Basic Info */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Email Details</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Campaign Name
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Internal campaign name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Type
                  </label>
                  <select
                    value={emailType}
                    onChange={(e) => setEmailType(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="newsletter">Newsletter</option>
                    <option value="promotional">Promotional</option>
                    <option value="transactional">Transactional</option>
                    <option value="drip">Drip Campaign</option>
                    <option value="announcement">Announcement</option>
                    <option value="digest">Digest</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject Line
                  </label>
                  <input
                    type="text"
                    value={subjectLine}
                    onChange={(e) => setSubjectLine(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Your subject line here..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {subjectLine.length} characters
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preview Text
                  </label>
                  <textarea
                    value={previewText}
                    onChange={(e) => setPreviewText(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={2}
                    placeholder="Preview text shown in inbox..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {previewText.length} characters
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description / Notes
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    placeholder="Internal notes..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Users className="w-4 h-4 inline mr-1" />
                    Recipients Count
                  </label>
                  <input
                    type="number"
                    value={recipientsCount}
                    onChange={(e) => setRecipientsCount(parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Scheduled Date
                  </label>
                  <input
                    type="date"
                    value={scheduledFor}
                    onChange={(e) => setScheduledFor(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Attachments */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Attachments</h2>

              <div className="space-y-3">
                {attachments.length > 0 ? (
                  attachments.map((asset) => (
                    <div key={asset.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-gray-700 truncate">{asset.file_name}</span>
                      </div>
                      <button
                        onClick={() => handleDeleteAsset(asset.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No attachments</p>
                )}

                <label className="block">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 cursor-pointer transition-colors">
                    <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                    <p className="text-xs text-gray-600">
                      {uploadingFiles.length > 0
                        ? `${uploadingFiles.length} file(s) selected`
                        : 'Click to upload attachments'}
                    </p>
                  </div>
                </label>

                {uploadingFiles.length > 0 && (
                  <div className="flex gap-2">
                    <button
                      onClick={handleUpload}
                      disabled={saving}
                      className="flex-1 px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                    >
                      Upload
                    </button>
                    <button
                      onClick={() => setUploadingFiles([])}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Email Body */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-6 border-b">
                <h2 className="text-lg font-semibold text-gray-900">Email Body</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Design your email content with rich text formatting
                </p>
              </div>

              <div className="p-6">
                <ReactQuill
                  theme="snow"
                  value={emailBody}
                  onChange={setEmailBody}
                  modules={quillModules}
                  className="h-96"
                  placeholder="Write your email content here..."
                />
              </div>

              {/* HTML View */}
              <div className="p-6 border-t">
                <details className="group">
                  <summary className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                    <Code className="w-4 h-4" />
                    View HTML Source
                  </summary>
                  <div className="mt-3">
                    <textarea
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg font-mono text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows={12}
                    />
                  </div>
                </details>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
